import { ref, watch, onMounted } from 'vue';
import { debounce } from 'lodash';

/**
 * Composable for loading entity detail data
 *
 * @param {String|Function} baseUrl - API endpoint or function returning URL
 * @param {Ref|Function} id - Entity ID (ref or getter function)
 * @param {Object} options - Configuration options
 * @param {Boolean} options.immediate - Load immediately on mount (default: true)
 * @param {Number} options.debounceMs - Debounce delay in ms (default: 300)
 * @param {Function} options.transform - Transform function for response data
 */
export function useEntityDetail(baseUrl, id, options = {}) {
    const { immediate = true, debounceMs = 300, transform = null } = options;

    const data = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const lastId = ref(null);

    /**
     * Get the current ID value
     */
    function getId() {
        if (typeof id === 'function') {
            return id();
        }
        return id?.value ?? id;
    }

    /**
     * Get the URL for the current request
     */
    function getUrl() {
        const currentId = getId();
        if (typeof baseUrl === 'function') {
            return baseUrl(currentId);
        }
        return `${baseUrl}/${currentId}`;
    }

    /**
     * Load entity data from API
     */
    async function load() {
        const currentId = getId();

        if (!currentId || currentId === lastId.value) {
            return;
        }

        loading.value = true;
        error.value = null;
        lastId.value = currentId;

        try {
            const response = await axios.get(getUrl());
            let responseData = response.data;

            // Apply transform if provided
            if (transform) {
                responseData = transform(responseData);
            }

            data.value = responseData;
        } catch (err) {
            error.value = err;
            new AxiosError(err.response).manage();
        } finally {
            loading.value = false;
        }
    }

    /**
     * Debounced load function
     */
    const debouncedLoad = debounce(load, debounceMs);

    /**
     * Force refresh the data (bypass debounce and lastId check)
     */
    async function refresh() {
        lastId.value = null;
        await load();
    }

    /**
     * Clear the data
     */
    function clear() {
        data.value = null;
        lastId.value = null;
        error.value = null;
    }

    // Watch for ID changes if it's a ref
    if (id && typeof id !== 'function' && id.value !== undefined) {
        watch(id, () => {
            debouncedLoad();
        });
    }

    // Load on mount if immediate
    if (immediate) {
        onMounted(() => {
            load();
        });
    }

    return {
        data,
        loading,
        error,
        load,
        refresh,
        clear,
        debouncedLoad
    };
}
